import React, { useState } from 'react';
import { getStayPlan } from '../../api/localIntelligence';
import { fmtDate, telHref } from '../shared/format';
import { ErrorState } from '../shared/StateMessage';
import { SkeletonText } from '../shared/Skeleton';
import InfoTip from '../shared/InfoTip';
import PartyChips from './ask/PartyChips';
import { useLocalActions } from './LocalActions';
import { BTN, BTN_PRIMARY, CARD_TITLE, PILL } from './liStyles';

// Plan a stay — a day-by-day plan of meals and outings for a guest's whole stay,
// using only places ready to send guests and never repeating one. The plan is the
// database's; this renders it.
const NIGHTS = Array.from({ length: 14 }, (_, i) => i + 1);

// Default arrival = tomorrow (today + 1 day). Bases "today" on the property's
// as_of date (the same source the form previously defaulted to as a raw
// string), then adds one calendar day and formats as YYYY-MM-DD via the shared
// fmtDate (local-time formatting — avoids the UTC date shift west of UTC, same
// approach as fmtDisplayDate in shared/format). A YYYY-MM-DD as_of is parsed at
// local midnight; a full ISO timestamp is parsed as-is. Falls back to the
// browser's today when as_of is missing, and to the raw asOf string if it
// can't be parsed.
const arrivalDefault = (asOf) => {
  const base = asOf
    ? new Date(asOf.includes('T') ? asOf : `${asOf}T00:00:00`)
    : new Date();
  if (Number.isNaN(base.getTime())) return asOf || '';
  base.setDate(base.getDate() + 1);
  return fmtDate(base);
};

// Friendly, generic error copy for any fn_li_stay_plan failure (timeout,
// network error, etc.). The raw Postgres error string (e.g. "Local
// Intelligence: canceling statement due to statement timeout") never reaches
// the user — only this fixed message renders, with the existing "Try again"
// button (ErrorState's onRetry re-triggers build()). work-page-fixes Item 2.
const STAY_PLAN_ERROR = 'This is taking longer than expected. Try again, or narrow the window.';

const StayPlan = ({ clientId, asOf, partyOptions, initialParty, onOpen, onBook, onOutcome }) => {
  const [arrival, setArrival] = useState(arrivalDefault(asOf));
  const [nights, setNights] = useState(2);
  const [party, setParty] = useState(initialParty || {});
  const [state, setState] = useState({ loading: false, error: null, data: null });

  const build = () => {
    if (!clientId || !arrival) return;
    setState({ loading: true, error: null, data: null });
    getStayPlan(clientId, arrival, nights, party)
      .then((data) => setState({ loading: false, error: null, data }))
      .catch(() => setState({ loading: false, error: STAY_PLAN_ERROR, data: null }));
  };

  const days = Array.isArray(state.data?.days) ? state.data.days : [];

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 space-y-4" data-testid="li-stay-plan">
      <div className="min-w-0">
        <h2 className={`${CARD_TITLE} text-lg`}>
          Planning a Longer Stay?
          <InfoTip text="Build a day-by-day plan of meals and outings for the whole stay, using only places ready to send guests." />
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Build a day-by-day plan of meals and outings for the whole stay, using only places ready to send guests.
        </p>
      </div>
      {/* Form — stacked layout. Arriving + Nights sit side by side on one row
          with a small gap; Party chips on the next row, Access chips on the row
          after that (PartyChips stacks Party over Access); and the Plan the stay
          button right-aligned on the Access row via justify-between. Consistent
          compact vertical spacing between rows (space-y-2). The panel is expanded
          by default — a three-night itinerary auto-builds on mount (see the
          useEffect above), so the day columns show real data on load. */}
      <div className="space-y-2">
        {/* Arriving date + Nights, side by side on one row with a small gap. */}
        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
          <label className="flex flex-col gap-1 text-sm text-gray-600 dark:text-gray-300">
            Arriving
            <input
              type="date"
              value={arrival}
              onChange={(e) => setArrival(e.target.value)}
              className="h-9 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 text-sm text-gray-900 dark:text-gray-100"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm text-gray-600 dark:text-gray-300">
            Nights
            <select
              value={nights}
              onChange={(e) => setNights(Number(e.target.value))}
              className="h-9 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 text-sm text-gray-900 dark:text-gray-100"
            >
              {NIGHTS.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
        </div>
        {/* Party chips on one row, Access chips on the next, with the Plan the
            stay button right-aligned on the Access row. */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div className="min-w-0">
            <PartyChips options={partyOptions} party={party} onChange={setParty} />
          </div>
          <button type="button" className={BTN_PRIMARY} onClick={build} disabled={!arrival || state.loading}>
            {state.loading ? 'Planning…' : 'Plan the stay'}
          </button>
        </div>
      </div>

      {state.loading && <SkeletonText lines={4} />}
      {state.error && <ErrorState message={state.error} variant="strip" onRetry={build} />}

      {days.length > 0 && (
        <div className="space-y-3">
          {/* Item 2 — itinerary as side-by-side day columns (one column per day)
              instead of a vertical day-grouped list. Equal-width columns via an
              auto-fit grid (same pattern as the page's stat tiles), so a 3-day
              stay fills three columns evenly and longer stays wrap. Each column
              has a colored header cycling blue → green → orange (matching the
              stat tile color tokens), with activities stacked below. */}
          <div className="grid [grid-template-columns:repeat(auto-fit,minmax(240px,1fr))] gap-3 items-start">
            {days.map((day, dayIdx) => (
              <DayColumn key={day.date} day={day} dayIdx={dayIdx} onOpen={onOpen} onOutcome={onOutcome} />
            ))}
          </div>
          {state.data?.note_label && <p className="text-xs text-gray-500 dark:text-gray-400">{state.data.note_label}</p>}
        </div>
      )}
    </div>
  );
};

// Item 2 — itinerary helpers. The itinerary renders as side-by-side day columns
// (DayColumn) with a colored header per day and compact activity entries
// (ActivityEntry) stacked within. ActivityEntry keeps the Item 1 essentials —
// place name, open-until + drive chips (HelpRail style), Call + Directions only —
// and adds a small type icon, the activity label + time line, and a category +
// rating line. No READY TO SEND GUESTS badge, Hours verified text, Quietest
// chip, or Share/Call to book/Send to guest/Order/Menu buttons — those belong to
// live search results, not a pre-built trip plan.

const ITINERARY_CHIP = 'text-sm px-2 py-0.5 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 whitespace-nowrap';
const openExternal = { target: '_blank', rel: 'noopener noreferrer' };

// "Thursday, Oct 1" → "THU, OCT 1". Falls back to the raw label uppercased when
// the shape ever differs.
const formatDayHeader = (label) => {
  const m = String(label || '').match(/^(\w+),\s*(.+)$/);
  if (!m) return String(label || '').toUpperCase();
  return `${m[1].slice(0, 3).toUpperCase()}, ${m[2].toUpperCase()}`;
};

// Day header accent colors cycle blue → green → orange (day 0 = blue, day 1 =
// green, day 2 = orange, day 3 = blue, …), matching the stat tile color tokens
// (TONE_STYLES in src/components/shared/MetricCardShell.js): brandBlue #32B1FF,
// emerald-500, amber-500. Each entry drives a 3px top border, a subtle tinted
// background, and the day-name text color — the same accent treatment the stat
// tiles use (border-t + tint + colored value).
const DAY_ACCENTS = [
  { bar: 'border-t-[#32B1FF] bg-[#32B1FF]/10', text: 'text-[#32B1FF]' },
  { bar: 'border-t-emerald-500 bg-emerald-500/10', text: 'text-emerald-600 dark:text-emerald-400' },
  { bar: 'border-t-amber-500 bg-amber-500/10', text: 'text-amber-600 dark:text-amber-400' },
];

// Inline activity glyphs (Feather/Lucide style — no icon library is installed,
// matching HelpRail.js / PartyChips.js). One per activity kind, matched against
// the slot label: coffee for "Morning coffee", utensils for meals
// ("Arrival dinner"/"Dinner"), compass for outings ("The big thing"/"Before you go").
const Glyph = ({ children }) => (
  <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);
const MealIcon = () => <Glyph><path d="M3 2v7c0 1.1.9 2 2 2h0a2 2 0 0 0 2-2V2" /><path d="M7 2v20" /><path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7" /></Glyph>;
const CoffeeIcon = () => <Glyph><path d="M18 8h1a4 4 0 0 1 0 8h-1" /><path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4Z" /><path d="M6 1v3" /><path d="M10 1v3" /><path d="M14 1v3" /></Glyph>;
const CompassIcon = () => <Glyph><circle cx="12" cy="12" r="10" /><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" /></Glyph>;

const activityIcon = (slotLabel) => {
  const s = String(slotLabel || '').toLowerCase();
  if (/coffee/.test(s)) return <CoffeeIcon />;
  if (/dinner|lunch|breakfast|brunch|meal/.test(s)) return <MealIcon />;
  return <CompassIcon />;
};

// Rating number is pulled from the pick's reasons string ("4.9 from 194 reviews")
// because plan picks carry no structured rating field — same approach AnswerCard
// uses for answer results.
const RATING_RE = /^([\d.]+)\s+from\s+[\d,]+\s+reviews/i;
const ratingFrom = (pick) => {
  const reasons = Array.isArray(pick?.reasons) ? pick.reasons : [];
  const r = reasons.find((x) => RATING_RE.test(x));
  return r ? r.match(RATING_RE)[1] : null;
};

const ActivityEntry = ({ slot, onOpen, onOutcome }) => {
  const { onCall } = useLocalActions();
  const pick = slot.pick;
  const actions = pick?.actions || {};
  const tel = telHref(actions.call || pick?.phone);
  const open = () => pick && onOpen && onOpen(pick);
  const log = (outcome) => { if (onOutcome && pick?.recommendation_id) onOutcome(pick.recommendation_id, outcome); };
  const rating = ratingFrom(pick);
  const catRating = [pick?.category_label, rating].filter(Boolean).join(' · ');
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => { if (e.key === 'Enter') open(); }}
      data-testid="li-itinerary-activity"
      className="group rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-3 text-left cursor-pointer transition-colors hover:border-[#32B1FF]/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]/50"
    >
      <div className="space-y-1.5">
        {/* Activity label + time with a small type icon. */}
        <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400">
          <span className="text-[#32B1FF] shrink-0">{activityIcon(slot.slot)}</span>
          <span className="min-w-0 truncate">{slot.slot}{slot.time_label ? ` · ${slot.time_label}` : ''}</span>
        </div>
        {/* Place name as bold headline. */}
        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 min-w-0">{pick?.name}</p>
        {/* Category + rating line, when available in the plan data. */}
        {catRating && <p className="text-xs text-gray-500 dark:text-gray-400 min-w-0">{catRating}</p>}
        {/* Open-until + drive chips (Essential Services Nearby style). */}
        {(pick?.hours_label || pick?.distance_label) && (
          <div className="flex flex-wrap gap-1.5">
            {pick?.hours_label && <span className={ITINERARY_CHIP}>{pick.hours_label}</span>}
            {pick?.distance_label && <span className={ITINERARY_CHIP}>{pick.distance_label}</span>}
          </div>
        )}
        {/* Call + Directions only. */}
        <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} role="presentation" className="flex flex-nowrap items-center gap-1">
          {tel && (onCall ? (
            <button type="button" className={`${BTN_PRIMARY} !px-2.5 !py-1`} onClick={() => onCall(pick)}>Call</button>
          ) : (
            <a href={tel} className={`${BTN_PRIMARY} !px-2.5 !py-1`} onClick={() => log('called')}>Call</a>
          ))}
          {actions.directions && <a href={actions.directions} {...openExternal} className={`${BTN} !px-2.5 !py-1`}>Directions</a>}
        </div>
      </div>
    </div>
  );
};

const DayColumn = ({ day, dayIdx, onOpen, onOutcome }) => {
  const accent = DAY_ACCENTS[dayIdx % DAY_ACCENTS.length];
  return (
    <section aria-label={day.day_label} className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden flex flex-col">
      {/* Colored day header — cycles blue → green → orange (stat tile tokens). */}
      <div className={`flex items-center gap-2 border-t-[3px] ${accent.bar} px-3 py-2`}>
        <h4 className={`text-xs font-bold uppercase tracking-wide ${accent.text}`}>{formatDayHeader(day.day_label)}</h4>
        {day.rain_expected && <span className={PILL.gray}>Rain</span>}
      </div>
      {/* Activities stack vertically within the day column. */}
      <div className="p-2 space-y-2 flex-1">
        {(day.slots || []).map((slot) => (
          <div key={`${day.date}-${slot.slot}`}>
            {slot.pick ? (
              <ActivityEntry slot={slot} onOpen={onOpen} onOutcome={onOutcome} />
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400 px-1 py-2">{slot.none_fit_label}</p>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

export default StayPlan;
